import { useEffect, useRef, type RefObject } from 'react';
import { prefersReducedMotion } from '@/components/layout/shell/entrance';
import { LEAF_D, MARK_HALF } from './geometry';

/*
  LeafGround — листья, выпавшие из окон знака на «землю» сцены входа (идея Максима). Листопад
  идёт за окнами; время от времени лист выпадает из нижнего окна к нам, падает, покачиваясь и
  кувыркаясь, и ложится внизу. На земле не больше 30 листьев: новые ложатся, старые тают. Ведёшь
  мышью (или пальцем) — листья разлетаются по ходу движения, кувыркаются и снова ложатся.

  Один холст на всю сцену, без DOM на каждый лист. Когда всё лежит — анимация засыпает и не тратит
  ни кадра. Меньше движения в системе — листопада на землю нет.
*/

interface LeafGroundProps {
  /** Сцена: в её границах лежит земля и ловится указатель. */
  stageRef: RefObject<HTMLElement | null>;
  /** Знак: из его нижних окон выпадают листья. */
  markRef: RefObject<HTMLElement | null>;
  /** Новые листья выпадают (глубина открыта). Лежащие остаются в любом случае. */
  active: boolean;
}

interface Leaf {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  size: number;
  color: string;
  state: 'fall' | 'air' | 'rest' | 'fade';
  /** Где лист ляжет: расстояние от низа сцены. */
  ground: number;
  phase: number;
  alpha: number;
  born: number;
}

const MAX_ON_GROUND = 30;
const SPAWN_MIN_MS = 2200;
const SPAWN_SPREAD_MS = 1000;
/** Нижние окна: 4, 6 и 8 часов. */
const LOW_WINDOWS = [5, 0, 1];

export function LeafGround({ stageRef, markRef, active }: LeafGroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const activeRef = useRef(active);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !stage || !ctx || prefersReducedMotion()) return;

    const css = getComputedStyle(document.documentElement);
    const colors = ['--brand-core', '--brand-core-light', '--brand-peach'].map((v) =>
      css.getPropertyValue(v).trim(),
    );
    const shape = new Path2D(LEAF_D);
    const leaves: Leaf[] = [];
    let W = 0;
    let H = 0;
    let dpr = 1;
    let raf = 0;
    let running = false;
    let last = 0;
    let spawnTimer = 0;

    const resize = () => {
      const r = stage.getBoundingClientRect();
      dpr = window.devicePixelRatio || 1;
      W = r.width;
      H = r.height;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      draw();
    };

    const draw = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      for (const l of leaves) {
        ctx.save();
        ctx.globalAlpha = l.state === 'rest' || l.state === 'fade' ? l.alpha * 0.88 : l.alpha;
        ctx.translate(l.x, l.y);
        ctx.rotate(l.rot);
        ctx.scale(l.size, l.size);
        if (l.state === 'fall' || l.state === 'air') {
          // В полёте лист светится, как за окнами; лежащий — без свечения.
          ctx.shadowColor = l.color;
          ctx.shadowBlur = 12;
        }
        ctx.fillStyle = l.color;
        ctx.fill(shape);
        ctx.restore();
      }
    };

    const step = (dt: number, t: number) => {
      for (const l of leaves) {
        const ground = H - l.ground;
        if (l.state === 'fall') {
          l.vy = Math.min(l.vy + 160 * dt, 52 + l.size * 14);
          l.x += (l.vx + Math.sin(t * 1.7 + l.phase) * 34) * dt;
          l.y += l.vy * dt;
          l.rot += l.vr * dt;
          l.alpha = Math.min(1, l.alpha + dt * 2.2);
          if (l.y >= ground) settle(l, ground, t);
        } else if (l.state === 'air') {
          l.vy += 780 * dt;
          l.vx *= 1 - 1.6 * dt;
          l.vr *= 1 - 1.1 * dt;
          l.x += l.vx * dt;
          l.y += l.vy * dt;
          l.rot += l.vr * dt;
          if (l.x < 10 || l.x > W - 10) {
            l.x = Math.min(W - 10, Math.max(10, l.x));
            l.vx *= -0.4;
          }
          if (l.vy > 0 && l.y >= ground) settle(l, ground, t);
        } else if (l.state === 'fade') {
          l.alpha -= dt / 1.2;
        }
      }
      for (let i = leaves.length - 1; i >= 0; i--)
        if (leaves[i].state === 'fade' && leaves[i].alpha <= 0) leaves.splice(i, 1);
    };

    const settle = (l: Leaf, ground: number, t: number) => {
      l.y = ground;
      l.vx = 0;
      l.vy = 0;
      l.vr = 0;
      l.state = 'rest';
      l.born = t;
      // Земля не копит листья: самые давние тают.
      const resting = leaves.filter((x) => x.state === 'rest').sort((a, b) => a.born - b.born);
      for (let i = 0; i < resting.length - MAX_ON_GROUND; i++) resting[i].state = 'fade';
    };

    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      step(dt, now / 1000);
      draw();
      if (leaves.some((l) => l.state !== 'rest')) raf = requestAnimationFrame(tick);
      else running = false;
    };

    const wake = () => {
      if (running) return;
      running = true;
      last = performance.now();
      raf = requestAnimationFrame(tick);
    };

    const spawn = () => {
      const m = markRef.current?.getBoundingClientRect();
      const s = stage.getBoundingClientRect();
      if (!m || m.width === 0) return;
      const scale = m.width / (MARK_HALF * 2);
      const k = LOW_WINDOWS[Math.floor(Math.random() * LOW_WINDOWS.length)];
      const a = (k * 60 * Math.PI) / 180;
      // Точка внутри окна на его средней линии (лепесток с поворотом 0 лежит внизу).
      const px = 10;
      const py = 290 + Math.random() * 130;
      leaves.push({
        x: m.left - s.left + m.width / 2 + (px * Math.cos(a) - py * Math.sin(a)) * scale,
        y: m.top - s.top + m.height / 2 + (px * Math.sin(a) + py * Math.cos(a)) * scale,
        vx: -12 - Math.random() * 22,
        vy: 12,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 2.4,
        size: 1.05 + Math.random() * 0.7,
        color: colors[Math.floor(Math.random() * colors.length)],
        state: 'fall',
        ground: 22 + Math.random() * 84,
        phase: Math.random() * Math.PI * 2,
        alpha: 0,
        born: 0,
      });
      wake();
    };

    const schedule = () => {
      spawnTimer = window.setTimeout(
        () => {
          if (activeRef.current && !document.hidden) spawn();
          schedule();
        },
        SPAWN_MIN_MS + Math.random() * SPAWN_SPREAD_MS,
      );
    };

    // Указатель разбрасывает листья по ходу движения: чем быстрее ведёшь, тем дальше летят.
    let prev: { x: number; y: number; t: number } | null = null;
    const onMove = (e: PointerEvent) => {
      const s = stage.getBoundingClientRect();
      const x = e.clientX - s.left;
      const y = e.clientY - s.top;
      const t = performance.now();
      if (prev) {
        const dt = Math.max(8, t - prev.t);
        const vx = ((x - prev.x) / dt) * 1000;
        const vy = ((y - prev.y) / dt) * 1000;
        const speed = Math.hypot(vx, vy);
        if (speed > 80) {
          let touched = false;
          for (const l of leaves) {
            if ((l.state !== 'rest' && l.state !== 'air') || Math.hypot(l.x - x, l.y - y) > 64) continue;
            // Толчок ограничен: даже резкий взмах подбрасывает лист невысоко — шорох, а не взрыв.
            const push = Math.min(1, speed / 1600);
            const k = Math.min(speed, 1600) / Math.max(speed, 1);
            l.vx = vx * k * 0.35 * push + (Math.random() - 0.5) * 80;
            l.vy = -(110 + Math.min(speed, 1600) * 0.18 * push + Math.random() * 90);
            l.vr = (Math.random() - 0.5) * 14;
            l.state = 'air';
            touched = true;
          }
          if (touched) wake();
        }
      }
      prev = { x, y, t };
    };
    const onLeave = () => {
      prev = null;
    };

    const ro = new ResizeObserver(resize);
    ro.observe(stage);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerleave', onLeave);
    schedule();
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(spawnTimer);
      ro.disconnect();
      stage.removeEventListener('pointermove', onMove);
      stage.removeEventListener('pointerleave', onLeave);
    };
  }, [stageRef, markRef]);

  return <canvas ref={canvasRef} aria-hidden className="pointer-events-none absolute inset-0 size-full" />;
}
